// ── AuthGate ─────────────────────────────────────────────────────────────────
// Wraps the app. Behavior:
//   • Firebase NOT configured        → render children as-is (local-only, no gate).
//   • Configured, no account yet AND
//     onboarding hasn't been done    → render children. A first-ever install
//     has never seen sign-in at all yet — OnboardingFlow runs first and asks
//     to sign in itself, right after its preview pages (see the "auth" phase
//     there) and before the personalize quiz, instead
//     of this gate blocking the app before onboarding gets a chance to show.
//   • Configured, logged out, but
//     onboarding was already done    → cute sign-in screen (Google + email/
//     password) — a returning/reinstalled/signed-out user who's seen the
//     quiz before goes straight back to signing in, same as before.
//   • Configured + logged in         → run initial cloud sync, then render the app.
//   • Loading / syncing              → tiny splash so we don't flash the wrong screen.
import React, { useEffect, useState } from 'react';
import { isFirebaseConfigured } from './config';
import { useAuth } from './auth';
import { startSync, safeReload } from './sync';
import { setAccountHint } from '../google/calendar';
import LoginScreen from './LoginScreen';
import { isOnboardingDone } from '../onboarding/onboardingStorage';

const LOADER_BG = '#f6f7fb';

function Splash({ label }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column', gap: 22,
      alignItems: 'center', justifyContent: 'center', background: LOADER_BG,
      fontFamily: 'Quicksand, Nunito, system-ui, sans-serif',
    }}>
      <img
        src="/ekkant-spinner-8565.gif"
        alt="Loading"
        style={{ width: 96, height: 96, objectFit: 'contain' }}
      />

      {label && (
        <span style={{ fontSize: 14, letterSpacing: 0.3, color: '#7b80a6', fontWeight: 600 }}>
          {label}
        </span>
      )}
    </div>
  );
}

// True if this device already has Clarity data saved locally. When it does, we
// can render the app instantly and sync in the background instead of blocking.
function hasLocalData() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('clarity_')) return true;
    }
  } catch { /* localStorage unavailable */ }
  return false;
}

// Floor on how long the splash stays up, so it doesn't flash by unseen when
// auth resolves from a cached session in a few hundred ms.
const MIN_SPLASH_MS = 1200;

export default function AuthGate({ children }) {
  const { user, loading } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [syncedUid, setSyncedUid] = useState(null);
  // Captured once: did we already have local data when the app booted? If so,
  // reopening should feel like resuming — render now, reconcile the cloud quietly.
  const [hadLocalData] = useState(hasLocalData);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMinTimeElapsed(true), MIN_SPLASH_MS);
    return () => clearTimeout(t);
  }, []);

  // Mirror the signed-in Google account to the Calendar layer so connecting
  // Google Calendar goes straight to the same account — no chooser. (Harmless
  // for email/password logins: the hint just won't match a Google session.)
  useEffect(() => {
    if (user?.email) setAccountHint(user.email);
  }, [user]);

  // On login, reconcile cloud data. We DON'T block the UI behind this when local
  // data already exists — the app reads the offline-first localStorage copy
  // immediately, so dismissing + reopening the app resumes instantly instead of
  // flashing a loader. Only a genuine cross-device change triggers one refresh.
  useEffect(() => {
    if (!isFirebaseConfigured || !user) return;
    if (syncedUid === user.uid) return;
    let cancelled = false;
    setSyncing(true);
    startSync(user.uid)
      .then((changed) => {
        // We rendered the app from stale local data; the cloud was newer. Pull it
        // in with a single refresh. (Same-device reopen → changed=false → seamless.)
        // Loop-protected: a sync that keeps reporting "changed" can't spin the
        // app into an endless reload/blink — safeReload throttles & caps reloads.
        if (changed && hadLocalData && !cancelled) safeReload();
      })
      .catch((e) => { /* eslint-disable-next-line no-console */ console.warn('[Clarity] initial sync failed', e); })
      .finally(() => { if (!cancelled) { setSyncedUid(user.uid); setSyncing(false); } });
    return () => { cancelled = true; };
  }, [user, syncedUid, hadLocalData]);

  // Local-only mode: no Firebase, no gate — behave exactly like before.
  if (!isFirebaseConfigured) return children;
  if (loading || !minTimeElapsed) return <Splash />;
  if (!user) {
    // Onboarding not done yet → this is (as far as this device knows) a
    // brand-new install that's never been asked to sign in. Let it through:
    // OnboardingFlow's own "auth" phase asks right after the previews instead.
    // A device that's already onboarded before (reinstall, manual sign-out)
    // skips straight back to this screen, same as pre-existing behavior.
    if (!isOnboardingDone()) return children;
    return <LoginScreen />;
  }
  // Block behind the splash ONLY on a first-ever login with nothing to show yet.
  // Returning users render straight from localStorage while sync runs in the bg.
  if ((syncing || syncedUid !== user.uid) && !hadLocalData) return <Splash />;
  return children;
}
